import { useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router'

import {
  AccordionRoot,
  Box,
  type BoxProps,
  AccordionItem as EcAccordionItem,
  AccordionItemContent as EcAccordionItemContent,
  AccordionItemTrigger as EcAccordionItemTrigger,
  Flex,
  LightModeSemanticColors,
  Text,
  VStack,
} from '@enechain/ecloud-designsystem'

import { AccordionDisclosureIcon } from '~/components/common/AccordionDisclosureIcon'
import {
  COMPACT_DATE_FIELD_WIDTH,
  CompactCivilDateField,
} from '~/components/common/CompactCivilDateField'
import { FieldLabel, FieldRoot } from '~/components/common/Field'
import { PageLayout } from '~/components/common/PageLayout'
import { AREA_ORDER, getMockItems } from '~/constants/supplyCapacityChange'
import { civilDateToInputValue, parseDateInputValue } from '~/models/civilDate'
import { now as serverNow } from '~/models/clock'
import { formatDateValue, parseLocalYmd } from '~/models/dateTime'
import { AreaTables } from '~/pages/SupplyCapacityChange/components/AreaTables'
import { ImpactCards } from '~/pages/SupplyCapacityChange/components/ImpactCards'
import { ImpactChart } from '~/pages/SupplyCapacityChange/components/ImpactChart'
import {
  buildAreaBlocks,
  buildCardModel,
  buildChartModel,
  fmtBadge,
  getDates,
  tomorrow,
} from '~/pages/SupplyCapacityChange/models/supplyCapacity'
import { routePaths } from '~/routes/routePaths'

// chakra v3.29 + React 18 types 既知不具合ワークアラウンド (SpotPrice 同形)
const AccordionItem = EcAccordionItem as React.FC<BoxProps & { value: string }>
const AccordionItemTrigger = EcAccordionItemTrigger as React.FC<BoxProps>
const AccordionItemContent = EcAccordionItemContent as React.FC<BoxProps>

/**
 * 供給力変化画面 — 発電所の計画停止 (開始/終了) によるエリア別供給力 (MW) 純変化と
 * スポット市場への価格圧力を可視化する読み取り専用画面。 スポット価格画面から遷移する。
 *
 * design SoT: design/bidding/supply-capacity-change.html
 * 実装スコープ: frontend mock parity のみ (proto/bff/backend は別 issue #1692)
 */
export const SupplyCapacityChangePage = (): React.ReactElement => {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()

  // 受渡日: ?delivery=YYYY-MM-DD (= スポット価格からの遷移) が有効ならそれ、 無ければ翌日。
  const initialDelivery = useMemo(() => {
    const fromQuery = parseLocalYmd(searchParams.get('delivery'))
    return formatDateValue(fromQuery ?? tomorrow(serverNow()))
    // searchParams は初期値のみ参照 (以後は date input が SoT)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const [deliveryStr, setDeliveryStr] = useState<string>(initialDelivery)

  const view = useMemo(() => {
    const delivery = parseLocalYmd(deliveryStr) ?? tomorrow(serverNow())
    const { today, tmr } = getDates(delivery)
    const items = getMockItems(today, tmr)
    return {
      today,
      tmr,
      chart: buildChartModel(items, today, tmr, AREA_ORDER, {
        today: t('page:supplyCapacityChange.impact.today'),
        tmr: t('page:supplyCapacityChange.impact.tomorrow'),
      }),
      todayCard: buildCardModel(items, today),
      tmrCard: buildCardModel(items, tmr),
      blocks: buildAreaBlocks(items, today, tmr, AREA_ORDER),
    }
  }, [deliveryStr, t])

  return (
    <PageLayout
      breadcrumbItems={[
        {
          label: t('common:nav.bidding'),
          to: routePaths.biddingSpot.path,
        },
        {
          label: t('common:route.biddingSpotPrice'),
          to: routePaths.biddingSpotPrice.path,
        },
        {
          label: t('common:route.biddingSupplyCapacityChange'),
          bold: true,
        },
      ]}
    >
      {/* 縦スクロール + 左右 padding は親 content Box に委譲 (SpotPrice 同方針) */}
      <VStack align="stretch" gap={4}>
        {/* タイトル + 受渡日 picker + 約定日/受渡日 badge */}
        {/* ページ上部の見出し「市場価格への影響」はセクション見出しと重複の
              ため削除 (2026-08-31 デザイナー指摘)。 受渡日と日付表示は左寄せ。 */}
        <Flex alignItems="center" flexWrap="wrap" gap={4}>
          <Flex alignItems="center" gap={1}>
            {/* 日付入力は共通 CompactCivilDateField (2026-08-12 デザイナー指摘:
                  Date picker 未使用箇所の DS 化)。可視ラベルをそのまま
                  <label htmlFor> にして accessible name を維持する。 */}
            {/* DS form label は FieldRoot (chakra field slot recipe) の context 必須。
                  orientation="horizontal" でラベルと picker を従来どおり横並びにする。
                  chakra の field recipe は horizontal 時に label へ既定の
                  `margin-end` を載せるため、 素で置くと**ラベル右が大きく開く**
                  (2026-09-01 デザイナー指摘)。 `marginEnd={0}` で打ち消し、 間隔は
                  root の `gap` 側だけで決める (`SimilarDayDialog` と同じ処方)。 */}
            <FieldRoot
              alignItems="center"
              justifyContent="flex-start"
              gap={2}
              width="auto"
              orientation="horizontal"
            >
              {/* DS form label (2026-08-31 デザイナー指摘)。 FieldLabel の既定
                    (textStyle "label" = md/semibold/high-emphasis) をそのまま使う。 */}
              <FieldLabel
                flex="0 0 auto"
                marginEnd={0}
                whiteSpace="nowrap"
                htmlFor="supplyCapacityChange-deliveryDate"
              >
                {t('page:supplyCapacityChange.deliveryDate')}
              </FieldLabel>
              <Box width={COMPACT_DATE_FIELD_WIDTH}>
                {/* 受渡日は「常に値がある」画面軸 (SpotPageHeader / IntradayDeliveryDateControl
                      と同じ理由で hasClear は付けず、 空欄化は無視して直前値を
                      保持する。 PR #5323 レビュー、2026-08-13)。 */}
                <CompactCivilDateField
                  id="supplyCapacityChange-deliveryDate"
                  onChange={(d) => {
                    if (d !== undefined)
                      setDeliveryStr(civilDateToInputValue(d))
                  }}
                  value={parseDateInputValue(deliveryStr)}
                />
              </Box>
            </FieldRoot>
          </Flex>
          {/* [DS差異] 約定日/受渡日はラベル + 値。 design の `scc-date-badges` は
              色付き badge だが、 2026-08-31 デザイナー指摘 (第 1 弾「Badge UI 不要なら
              plain text に」 → 第 2 弾「ラベルは form label、 値はフォント md」) により
              label role (= FieldLabel と同じ canonical form label) + md の組へ変更。
              prototype は後追いで更新する。 */}
          <Flex flexWrap="wrap" gap={4}>
            <Flex alignItems="center" gap={1}>
              <Text textStyle="label">
                {t('page:supplyCapacityChange.contractDateBadge')}
              </Text>
              <Text color="Text.highEmphasis" fontSize="md">
                {fmtBadge(view.today)}
              </Text>
            </Flex>
            <Flex alignItems="center" gap={1}>
              <Text textStyle="label">
                {t('page:supplyCapacityChange.deliveryDateBadge')}
              </Text>
              <Text color="Text.highEmphasis" fontSize="md">
                {fmtBadge(view.tmr)}
              </Text>
            </Flex>
          </Flex>
        </Flex>

        <AccordionRoot defaultValue={['impact', 'area']} multiple>
          {/* セクション 1: 市場価格への影響 (chart + cards) */}
          {/* 器の枠・角丸は design `css/components.css:4206` の `.ecl-accordion`
              (border 1px --border-low / radius lg) 準拠。 chakra v3 の accordion
              recipe は item に下線を引くだけで枠も角丸も持たないため、 使う側で
              明示する (NormalBidTab の同コメント参照)。 無いと閉じた状態で
              カードの枠が消える (2026-08-31 デザイナー指摘: dev 全体)。
              content の border-top は同 `:4253` `.ecl-accordion-content` 準拠。 */}
          <AccordionItem
            overflow="hidden"
            borderWidth="1px"
            borderColor="Border.lowEmphasis"
            borderRadius="lg"
            backgroundColor={LightModeSemanticColors.Surface.primary.value}
            value="impact"
          >
            <AccordionItemTrigger paddingX={4} paddingY={3}>
              {/* 開閉標は header の**左端**。 DS `AccordionIconButton` を使う */}
              <AccordionDisclosureIcon />
              <Text textStyle="sectionTitle">
                {t('page:supplyCapacityChange.impact.title')}
                <Text
                  as="span"
                  textStyle="captionNormal"
                  marginLeft={2}
                  color="Text.midEmphasis"
                >
                  {t('page:supplyCapacityChange.impact.subtitle')}
                </Text>
              </Text>
            </AccordionItemTrigger>
            <AccordionItemContent
              borderTopWidth="1px"
              borderTopColor="Border.lowEmphasis"
              paddingX={3}
              paddingY={3}
            >
              {/* [DS差異] design は 1280px で chart/summary を縦積みする media query。
                  inline style では表現できないため flex-wrap で近似 (chart flex 1 / cards 360px)。 */}
              <Flex alignItems="stretch" flexWrap="wrap" gap={3}>
                <ImpactChart chart={view.chart} />
                <Flex flexDirection="column" flex="0 0 360px" minWidth="320px">
                  <ImpactCards
                    tmr={view.tmr}
                    tmrModel={view.tmrCard}
                    today={view.today}
                    todayModel={view.todayCard}
                  />
                </Flex>
              </Flex>
            </AccordionItemContent>
          </AccordionItem>

          {/* セクション 2: エリア別詳細 */}
          <AccordionItem
            overflow="hidden"
            marginTop={4}
            borderWidth="1px"
            borderColor="Border.lowEmphasis"
            borderRadius="lg"
            backgroundColor={LightModeSemanticColors.Surface.primary.value}
            value="area"
          >
            <AccordionItemTrigger paddingX={4} paddingY={3}>
              {/* 開閉標は header の**左端**。 DS `AccordionIconButton` を使う */}
              <AccordionDisclosureIcon />
              <Text textStyle="sectionTitle">
                {t('page:supplyCapacityChange.area.title')}
                <Text
                  as="span"
                  textStyle="captionNormal"
                  marginLeft={2}
                  color="Text.midEmphasis"
                >
                  {t('page:supplyCapacityChange.area.subtitle')}
                </Text>
              </Text>
            </AccordionItemTrigger>
            <AccordionItemContent
              borderTopWidth="1px"
              borderTopColor="Border.lowEmphasis"
              paddingX={3}
              paddingY={3}
            >
              <AreaTables blocks={view.blocks} />
            </AccordionItemContent>
          </AccordionItem>
        </AccordionRoot>
      </VStack>
    </PageLayout>
  )
}
